<template>
  <div class="flex flex-col">
    <div v-if="picklistOptions !== undefined" :style="styleObject">
      <VueDraggableNext
        v-model="localPicklistOptions"
        handle=".drag-handle"
        class="w-full"
        :style="{
          marginTop: field.style === OPTION_FIELD_STYLES.BUBBLE && !isActive ? '6px' : '0px',
        }"
        @change="onOptionsReorder"
      >
        <div
          v-for="(pickerOption, index) in localPicklistOptions"
          :key="index"
          :style="minimumWidth"
          class="option"
          :class="{ 'option-bubble': field.style === OPTION_FIELD_STYLES.BUBBLE }"
        >
          <div class="align-together flex items-start">
            <Menu05Icon
              v-if="isActive"
              class="drag-handle ml-0 mr-2 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500"
            />
            <div
              v-if="field.style === OPTION_FIELD_STYLES.BUBBLE"
              class="bubble-container form-control w-auto"
            >
              <input
                v-if="type === 'input'"
                :id="`checkbox-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                type="checkbox"
                :readonly="readonly"
                :value="typeof pickerOption === 'string' ? pickerOption : pickerOption.label"
                disabled
              />
              <label
                :id="`option-label-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                :ref="(el) => setLabelRef(el as HTMLElement | null, index)"
                :for="`checkbox-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                contenteditable="true"
                class="bubble-label"
                :style="[syncedLabelInlineStyle]"
                @beforeinput="measureAndSyncLabelWidths()"
                @input="
                  (e) => {
                    updateOptionLabel(e, index);
                    handleSafariCursor(e);
                    measureAndSyncLabelWidths();
                  }
                "
                @compositionend="measureAndSyncLabelWidths()"
                @paste="() => measureAndSyncLabelWidths()"
                @keydown="(e) => e.key !== 'Tab' && handleSafariCursor(e)"
                @keydown.capture="
                  (e) =>
                    handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
                "
                @keydown.enter.prevent.stop="handleEnterOnOption(index)"
                @keyup.enter.prevent.stop
                @keyup.tab.prevent.stop
                @keyup.capture="suppressTabKeyUp"
                @focus="handleFocus"
                @keyup="
                  (e) => {
                    handleEditing(e);
                    measureAndSyncLabelWidths();
                  }
                "
              >
                {{ typeof pickerOption === "string" ? pickerOption : pickerOption.label }}
              </label>
            </div>
            <template v-else>
              <input
                v-if="type === 'input'"
                :id="`checkbox-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                class="ml-0 mt-1 flex-shrink-0"
                type="checkbox"
                :readonly="readonly"
                :value="typeof pickerOption === 'string' ? pickerOption : pickerOption.label"
                disabled
              />
              <label
                :id="`checkbox-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                :key="resetKey"
                :for="`checkbox-${
                  field.id || field.uuid || componentUniqueId || ''
                }-${index}`"
                contenteditable="true"
                class="inline-block w-auto flex-grow cursor-text border-0 pr-2 shadow-none outline-none"
                @input="
                  (e) => {
                    handleLabelInput(e, index);
                  }
                "
                @compositionstart="handleCompositionStart"
                @compositionend="
                  (e) => {
                    compositionLabelEndHandler(e, index);
                  }
                "
                @keydown="(e) => e.key !== 'Tab' && handleSafariCursor(e)"
                @keydown.capture="
                  (e) =>
                    handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
                "
                @keydown.enter.prevent.stop="handleEnterOnOption(index)"
                @keyup.enter.prevent.stop
                @keyup.tab.prevent.stop
                @keyup.capture="suppressTabKeyUp"
                @focus="handleFocus"
                @keyup="handleEditing($event)"
              >
                {{ typeof pickerOption === "string" ? pickerOption : pickerOption.label }}
              </label>
            </template>
            <Trash01Icon
              v-if="isActive && !(field?.model && field.model.startsWith('custom_objects'))"
              class="ml-2 mt-1 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
              @click="prepareToRemoveOption(index)"
            />
          </div>
        </div>
      </VueDraggableNext>
      <div
        v-if="shouldShowCustomOption"
        :style="minimumWidth"
        class="option"
        :class="{ 'option-bubble': field.style === OPTION_FIELD_STYLES.BUBBLE }"
      >
        <div class="align-together flex items-start">
          <Menu05Icon
            v-if="isActive"
            class="drag-handle ml-0 mr-2 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500 opacity-25"
          />
          <div
            v-if="field.style === OPTION_FIELD_STYLES.BUBBLE"
            class="bubble-container form-control w-auto"
          >
            <input
              v-if="type === 'input'"
              :id="`checkbox-${field.id}-other`"
              type="checkbox"
              :readonly="readonly"
              :value="otherLabel"
              disabled
            />
            <label
              :for="`checkbox-${field.id}-other`"
              class="bubble-label cursor-default"
              :style="[syncedLabelInlineStyle]"
            >
              {{ otherLabel }}
            </label>
          </div>
          <template v-else>
            <input
              v-if="type === 'input'"
              :id="`checkbox-${field.id}-other`"
              class="ml-0 mt-1 flex-shrink-0"
              type="checkbox"
              :readonly="readonly"
              :value="otherLabel"
              disabled
            />
            <label
              :for="`checkbox-${field.id}-other`"
              class="inline-block w-auto flex-grow cursor-default pr-2"
            >
              {{ otherLabel }}
            </label>
          </template>
          <Trash01Icon
            v-if="isActive && !(field?.model && field.model.startsWith('custom_objects'))"
            class="ml-2 mt-1 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
            @click="disableOtherOption"
          />
        </div>
        <div
          class="relative mt-2 w-full max-w-full"
          :class="{
            'ml-7': isActive,
          }"
          :style="otherInputContainerStyle"
        >
          <input
            v-if="!isActive"
            :id="`${field.id || 'checkbox'}-other-input`"
            class="other-input-control form-control box-border w-full max-w-full rounded-[10px]"
            type="text"
            :placeholder="otherPlaceholder"
          />
          <input
            v-else
            :id="`${field.id || 'checkbox'}-other-input`"
            class="other-input-control form-control box-border w-full max-w-full rounded-[10px]"
            type="text"
            :value="otherPlaceholderValue"
            @input="updateOtherPlaceholder"
            @focus="handleOtherPlaceholderFocus"
            @blur="commitOtherPlaceholder"
          />
          <span
            class="other-chip pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-indigo-100 px-2.5 py-1 text-xs leading-4 text-blue-700"
          >
            {{ otherLabel }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="isActive" class="mt-[-1rem] flex flex-wrap items-center gap-4">
      <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
      <UIButton
        v-if="canAddCustomOption"
        id="add-other-option"
        text
        class="mt-4 !text-gray-600 hover:!text-gray-700"
        @click="enableCustomOption"
      >
        <PlusIcon class="mr-1.5 h-5 w-5 !text-gray-600" />
        {{ t("common.addOtherOption") }}
      </UIButton>
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="field.shortLabel" class="short-label font-inter break-words text-sm">
        {{ field.shortLabel }}
      </span>
    </template>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import { useBubbleLabelSync } from "@/composition/useBubbleLabelSync";
import { useIMEComposition } from "@/composition/useIMEComposition";
import { usePicklistOptionEditor } from "@/composition/usePicklistOptionEditor";
import { useOtherOptionPlaceholderEditor } from "@/composition/useOtherOption";
import { useAppStore } from "@/store/app";
import { OPTION_FIELD_STYLES } from "@/util/constants";
import {
  handleOptionTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  suppressTabKeyUp,
} from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { computed, CSSProperties, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

const { t } = useI18n();
const store = useAppStore();

const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();

interface PickerOption {
  src?: string;
  label?: string;
  [key: string]: any;
}

const props = defineProps({
  type: {
    type: String,
    default: "input",
  },
  placeholder: {
    type: String,
    default: "",
  },
  picklistOptions: {
    type: Array as () => (string | PickerOption)[],
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: Number,
    default: 1,
  },
  field: {
    type: Object,
    default() {
      return {};
    },
  },
  styleObject: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
  hideCustomOption: {
    type: Boolean,
    default: false,
  },
});

const componentUniqueId = ref<string | null>(null);
if (!componentUniqueId.value && !props.field.id && !props.field.uuid) {
  componentUniqueId.value = String(Date.now());
}

const emit = defineEmits([
  "update:shortLabel",
  "update:picklistOptions",
  "update:allowCustomOption",
]);

const shortLabel = ref(props.field.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);

watch(
  () => props.field.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const isBubble = computed(() => props.field?.style === OPTION_FIELD_STYLES.BUBBLE);

const {
  localPicklistOptions,
  minimumWidth,
  isLastOptionEmpty,
  indexToRemove,
  showDeleteModal,
  addOption: _addOption,
  addOptionAt: _addOptionAt,
  prepareToRemoveOption,
  removeOption,
  cancelRemoveOption,
  updateOptionLabel: _updateOptionLabel,
  handleFocus,
  handleEditing,
  handleSafariCursor,
} = usePicklistOptionEditor({
  picklistOptions: computed(() => props.picklistOptions),
  field: computed(() => props.field),
  layout: computed(() => props.layout),
  customFieldCreated: computed(() => props.customFieldCreated),
  t,
  onOptionsChange: (options) => emit("update:picklistOptions", options),
});

const { setLabelRef, getLabelRef, syncedLabelInlineStyle, measureAndSyncLabelWidths } =
  useBubbleLabelSync({
    picklistOptions: localPicklistOptions,
    fieldStyle: computed(() => props.field?.style),
    isBubble,
    baseMinWidth: 20,
  });

const onOptionsReorder = () => {
  measureAndSyncLabelWidths();
};

/**
 * @description Forwards `addOption` / `addOptionAt` / `updateOptionLabel` calls with the
 * `getLabelRef` getter so the composable can focus and restore caret position correctly
 * using the bubble-sync label refs.
 */
const addOption = () => _addOption(getLabelRef);
const addOptionAt = (index: number) => _addOptionAt(index, getLabelRef);

const updateOptionLabel = (event: Event, index: number) => {
  _updateOptionLabel(event, index, getLabelRef);
  measureAndSyncLabelWidths();
};

const {
  shouldShowCustomOption: shouldShowStoredCustomOption,
  otherLabel,
  otherPlaceholder,
  otherInputContainerStyle,
  otherPlaceholderValue,
  handleOtherPlaceholderFocus,
  updateOtherPlaceholder,
  commitOtherPlaceholder,
} = useOtherOptionPlaceholderEditor({
  element: computed(() => props.field),
  store,
  t,
  isActive: computed(() => props.isActive),
});

const shouldShowCustomOption = computed(() => {
  return shouldShowStoredCustomOption.value && !props.hideCustomOption;
});

const canAddCustomOption = computed(() => {
  return !shouldShowStoredCustomOption.value && !props.hideCustomOption;
});

const enableCustomOption = () => emit("update:allowCustomOption", true);
const disableOtherOption = () => emit("update:allowCustomOption", false);

/**
 * @description Raw label input handler: updates the option label and fixes the Safari cursor.
 * Wrapped with `withIMECheck` to skip processing during IME composition (e.g. CJK input).
 */
const rawHandleLabelInput = (e: Event, index: number) => {
  updateOptionLabel(e, index);
  handleSafariCursor(e);
};

const compositionLabelEndHandler = handleCompositionEnd(rawHandleLabelInput);
const handleLabelInput = withIMECheck(rawHandleLabelInput);

/**
 * @description Raw short-label input handler, wrapped with `withIMECheck`
 * and a composition-end handler for IME-safe editing.
 */
const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleEnterOnOption = (index: number) => addOptionAt(index);

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style scoped>
.option {
  position: relative;
  display: inline-block;
  widows: 100%;
}
.option label {
  line-height: 30px;
  display: block;
  margin-left: 10px;
}
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

.other-chip {
  border: 1px solid #c7d2fe;
}

/* Bubble Style Overrides for Checkboxes */
.option-bubble {
  margin-bottom: 8px;
}

.bubble-container {
  background: transparent;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  /* Keep checkbox and label on the same line so the checkbox stays
     vertically centered relative to multi-line labels */
  flex-wrap: nowrap;
  /* Add small horizontal spacing between bubbles */
  margin-left: 2px;
  margin-right: 2px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

/* Consistent checkbox visuals (bubble + standard) */
.option:not(.option-bubble) input[type="checkbox"],
.bubble-container input[type="checkbox"] {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #98a2b3;
  border-radius: 4px;
  background: #ffffff;
  display: inline-block;
  position: relative;
  margin: 0;
  flex-shrink: 0;
}
.option:not(.option-bubble) input[type="checkbox"]:checked,
.bubble-container input[type="checkbox"]:checked {
  background-color: #000000;
  border-color: #000000;
}

.bubble-container:hover {
  border-color: #d1d5db;
}

.bubble-container input[type="checkbox"]:checked ~ .bubble-label {
  color: #1e40af;
}

.bubble-label {
  background: transparent !important;
  border: none !important;
  padding: 0;
  line-height: 16px;
  display: flex;
  align-items: center;
  min-height: 16px;
  margin: 0 !important;
  cursor: text;
  outline: none;
  color: inherit;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.option-bubble .align-together {
  align-items: center !important;
}
.option-bubble .drag-handle,
.option-bubble .ml-2.mt-1 {
  margin-top: 0 !important;
  align-self: center;
}

.option:not(.option-bubble) .align-together {
  align-items: center !important;
}

.option:not(.option-bubble) .drag-handle,
.option:not(.option-bubble) input[type="checkbox"],
.option:not(.option-bubble) .ml-2.mt-1 {
  margin-top: 0 !important;
}

.option label {
  margin-bottom: 0 !important;
}
</style>
